import { FC, useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { Table } from '../../../components/table/index';
import { SafeHooks, useModalState, useScreenSize } from '../../../hooks/index';
import { selectedCompanyStorage } from '../../../storage/company/index';
import { safesSortStore } from '../../../storage/local/index';
import { SafeOrder, SafeSortBy } from '../../../types/safes.types';
import { TableSortingCell } from '../../safes/desktop/table-sorting-cell';
import { SelectedSafe } from '../../safes/index';
import { EditSafeModal } from '../../safes/modals/edit/index';
import { SafeItem } from './safe-item';
import { columnOrderMap, columnTranslationKey, sortColumn } from './safe-ledger.types';

type InvestmentLedgerProps = {
  stakeholderId: string;
};

export const SafeLedger: FC<InvestmentLedgerProps> = ({ stakeholderId }) => {
  const companyId = selectedCompanyStorage(({ companyId }) => companyId);

  const [sortState, setSortState] = useState<{
    field: null | SafeSortBy;
    sortMode: null | SafeOrder;
  }>(safesSortStore.get() || { field: null, sortMode: null });
  const [selectedSafe, setSelectedSafe] = useState<null | SelectedSafe>({
    id: '',
    step: 1,
    mode: 'EDIT',
  });

  const { isMobile } = useScreenSize();

  const { safes, invalidateQuery } = SafeHooks.useStakeholderSafes({
    currentPage: 1,
    sortOrder: sortState.sortMode,
    sortBy: sortState.field,
    companyId,
    stakeholderId,
    numberOfFetchedItems: 100,
    search: '',
    convertedOnly: false,
  });

  const handleSortField = useCallback(
    (newField: SafeSortBy) => {
      setSortState((prevState) => {
        if (prevState.field === newField) {
          const nextSortMode = !prevState.sortMode
            ? SafeOrder.ASC
            : prevState.sortMode === SafeOrder.ASC
              ? SafeOrder.DESC
              : null;

          safesSortStore.set({ ...prevState, sortMode: nextSortMode });
          return { ...prevState, sortMode: nextSortMode };
        } else {
          safesSortStore.set({
            field: newField,
            sortMode: SafeOrder.ASC,
          });
          return { field: newField, sortMode: SafeOrder.ASC };
        }
      });
      invalidateQuery();
    },
    [invalidateQuery],
  );

  const {
    handleCloseModal: handleCloseEditSafeModal,
    handleOpenModal: handleOpenEditSafeModal,
    isOpen: isOpenEditSafeModal,
  } = useModalState({});

  if (!safes.length) return;

  return (
    <div className="max flex w-full flex-col gap-3 rounded-lg bg-gray-50 p-1 !pt-3 lg:p-[2px]">
      <EditSafeModal
        companyId={companyId}
        handleClose={handleCloseEditSafeModal}
        invalidateQuery={invalidateQuery}
        isOpenModal={isOpenEditSafeModal}
        onEditClick={({ id, step }) => setSelectedSafe({ id, mode: 'EDIT', step })}
        previewMode={selectedSafe?.mode === 'VIEW' || undefined}
        safeId={selectedSafe?.id || ''}
        step={selectedSafe?.step || 1}
      />

      <span className="pl-4 text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-700">
        <AppFormattedMessage id="SAFE_PENDING_CONVERSION" />
      </span>

      <div
        className={twMerge(
          'flex gap-2 overflow-hidden rounded-md shadow-sm',
          !isMobile && 'bg-white px-2 pb-2 pt-1',
        )}
      >
        <div
          className={twMerge(
            'flex h-full w-full flex-col gap-1 overflow-x-auto rounded-md border border-gray-100',
          )}
        >
          {isMobile ? (
            safes.map((data) => <SafeItem data={data} key={data.id} />)
          ) : (
            <Table
              columnOrder={columnOrderMap}
              columnsTitle={Object.entries(columnTranslationKey).reduce(
                (acc, [key, payload]) => ({
                  ...acc,
                  [key]: sortColumn[key] ? (
                    <TableSortingCell
                      onSortClick={(newField) => handleSortField(newField)}
                      sortByCell={sortColumn[key]}
                      sortField={sortState.field}
                      sortMode={sortState.sortMode}
                      title={<AppFormattedMessage id={payload} />}
                    />
                  ) : (
                    payload
                  ),
                }),
                {},
              )}
            >
              {safes.map((data) => (
                <SafeItem
                  data={data}
                  key={data.id}
                  mode="TABLE"
                  openViewModal={() => {
                    handleOpenEditSafeModal();
                    setSelectedSafe({
                      id: data.id,
                      mode: 'VIEW',
                      step: 4,
                    });
                  }}
                />
              ))}
            </Table>
          )}
        </div>
      </div>
    </div>
  );
};
